import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PublicPlan } from '@neuroedge/shared';
import { Button, Modal } from '../../components/ui';
import { EnquiryForm } from '../../marketing/EnquiryForm';
import { usePlatformChat } from '../../marketing/usePlatformChat';
import { useAuth } from '../../auth/AuthContext';
import logo from '../../theme/NeuroEdgeLogo.png';
import { ContactDetails } from '../../marketing/ContactDetails';
import { PlansCatalog } from './PlansCatalog';

/**
 * Public pricing page (/pricing) - anyone, signed in or not, can see the
 * plans. Outside both route guards (like /privacy). Signed-out visitors are
 * offered a free trial; signed-in members are sent to the app.
 */
export function PricingPage() {
  const { state } = useAuth();
  const signedIn = state.status === 'authenticated';
  usePlatformChat();
  /** The enquiry popup: null = closed; a plan when asked from its card. */
  const [enquiry, setEnquiry] = useState<{ plan?: PublicPlan } | null>(null);

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: 'var(--space-6) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-5)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <Link
          to="/"
          aria-label="NeuroEdge Connect home"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontWeight: 700,
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          <img src={logo} alt="" style={{ height: 32, width: 'auto' }} />
          NeuroEdge Connect
        </Link>
        <Link to="/">← Back to home</Link>
      </div>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>NeuroEdge Connect plans</h1>
          <p style={{ margin: 'var(--space-1) 0 0', color: 'var(--color-text-muted)' }}>
            WhatsApp CRM, automation and ordering for your business. Every new workspace starts with
            a free trial. Prices are per workspace per month; WhatsApp message charges are billed by
            Meta to your own WhatsApp account.
          </p>
        </div>
        <nav style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={() => setEnquiry({})}>
            Send an enquiry
          </Button>
          {signedIn ? (
            <Link to="/" className="ne-btn ne-btn--primary">
              Go to the app
            </Link>
          ) : (
            <>
              <Link to="/login" className="ne-btn ne-btn--secondary">
                Sign in
              </Link>
              <Link to="/register" className="ne-btn ne-btn--primary">
                Start free trial
              </Link>
            </>
          )}
        </nav>
      </header>

      <PlansCatalog
        actionFor={(plan) =>
          plan.isCustomPricing ? (
            <Button variant="secondary" onClick={() => setEnquiry({ plan })}>
              Enquire
            </Button>
          ) : signedIn ? (
            <Link to="/plans" className="ne-btn ne-btn--secondary">
              See in the app
            </Link>
          ) : (
            <Link to="/register" className="ne-btn ne-btn--primary">
              Start free trial
            </Link>
          )
        }
      />

      <footer
        style={{
          display: 'grid',
          gap: 'var(--space-2)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
        }}
      >
        <span>
          Questions or Enterprise pricing? <ContactDetails inline />
        </span>
        <Link to="/privacy">Privacy Policy</Link>
      </footer>
      {enquiry ? (
        <Modal title="Send an enquiry" onClose={() => setEnquiry(null)}>
          <EnquiryForm planCode={enquiry.plan?.code} planName={enquiry.plan?.name} />
        </Modal>
      ) : null}
    </div>
  );
}
